import { useOutletContext, useRouteLoaderData, useSearchParams } from '@remix-run/react';
import { useMemo } from 'react';

import type { OutletContext } from '~/interfaces/outlet_context';

import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';

import type { RootLoaderData } from '~/root';
import type { PostLoaderData } from '~/routes/p/$slug';
import { PostPageContext } from './PostPageContext';

interface PostPageProviderProps {
  children: React.ReactNode;
}

export const PostPageProvider: React.FC<PostPageProviderProps> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const isPreviewModal = searchParams.get('preview_modal') === 'true';
  const { subscription } = useRouteLoaderData('root') as RootLoaderData;
  const { post, onlyRenderComments, commentsEnabled, isDraft, paywall, site, previewSubscription } =
    useTypedLoaderData<PostLoaderData>();
  const currentSubscription = subscription || previewSubscription;

  const publicationHostname = useMemo(() => {
    try {
      return new URL(site.publication.url).hostname;
    } catch (_error: unknown) {
      // do nothing
    }
  }, [site.publication]);

  const { cookies, isLoggedIn: isLoggedInOutletValue, admin } = useOutletContext<OutletContext>();

  const isLoggedIn = isLoggedInOutletValue || (!!currentSubscription && isPreviewModal);
  const isPopupDisabled = Boolean(cookies.popup_closed) || isLoggedIn;
  const isGatedContentDisabled = Boolean(cookies.email_capture_disabled) || isLoggedIn;
  const isPostPremium = post.audience === 'premium';
  const isAdmin = Boolean(admin);
  const isSubscriptionPremium = currentSubscription?.tier === 'premium';
  const hidePremiumComments = Boolean(isPostPremium && !isSubscriptionPremium && !isAdmin && !isDraft);
  const canShowCommentsNode = commentsEnabled && !hidePremiumComments;
  const showTruncated = post.contains_subscriber_break && !isGatedContentDisabled;

  const showSubscriberBreak = post.contains_subscriber_break && !isGatedContentDisabled;
  const showPaywallBreak = !!post.paywall_shown;
  const showPaywallAndHidePostContent = post.hide_premium_post;

  // Advanced Email Capture
  const isSubscriberGateEnabled =
    post.enforce_gated_content === true && !showSubscriberBreak && !isGatedContentDisabled;
  const isEmailCapturePopupEnabled =
    post.enable_popup_on_scroll === true && !showSubscriberBreak && !isGatedContentDisabled;

  // Recommended Reading State
  const showRecommendedPostsNode = !isDraft && !isSubscriberGateEnabled && !onlyRenderComments;

  // Audio block (text to speech)
  const showTextToSpeechBlock =
    !!post?.text_to_speech_config?.audio_url &&
    !isSubscriberGateEnabled &&
    !showSubscriberBreak &&
    !showPaywallBreak &&
    !showPaywallAndHidePostContent;

  const paywallUrl = useMemo(() => {
    if (typeof document === 'undefined') {
      return;
    }

    try {
      const baseUrl = new URL(`${document.location.origin}/upgrade${document.location.search}`);

      if (paywall?.offer_id) {
        baseUrl.searchParams.set('offer_id', paywall.offer_id);
      }
      return baseUrl.toString();
    } catch {
      return `${document.location.origin}/upgrade${document.location.search}`;
    }
  }, [paywall]);

  return (
    <PostPageContext.Provider
      value={{
        canShowCommentsNode,
        isGatedContentDisabled,
        onlyRenderComments,
        isPopupDisabled,
        hidePremiumComments,
        showRecommendedPostsNode,
        showTruncated,
        isSubscriberGateEnabled,
        isEmailCapturePopupEnabled,
        showSubscriberBreak,
        showPaywallBreak,
        showPaywallAndHidePostContent,
        paywallUrl,
        showTextToSpeechBlock,
        isSubscriptionPremium,
        utmParams: {
          utmParamsEnabled: site.publication.utm_params_enabled,
          utmCampaign: site.publication.utm_campaign || post.parameterized_web_title,
          utmMedium: site.publication.utm_medium || 'referral',
          utmSource: site.publication.utm_source || publicationHostname,
        },
      }}
    >
      {children}
    </PostPageContext.Provider>
  );
};
